<template>
  <div class="flex flex-col">
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12">
        <AppModal v-model="showAddManual" title="Create Manual">
          <form @submit.prevent="save">
            <div class="space-y-4">
              <div>
                <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Module/Section Name</label>
                <input
                  type="text"
                  v-model="form.name"
                  placeholder="Module/Section Name"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                  required
                />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Position</label>
                <input
                  type="number"
                  v-model="form.position"
                  placeholder="Position"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                  required
                />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Section Attachment</label>
                <input
                  type="file"
                  @change="handleFileChange"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                  required
                />
              </div>
              <div class="flex justify-end space-x-2">
                <button
                  type="button"
                  @click="closeForm"
                  class="btn-sm-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading"
                  class="btn-primary"
                >
                  <i class="fa-solid fa-save"></i>
                  <span>Save</span>
                </button>
              </div>
            </div>
          </form>
        </AppModal>

        <div class="flex items-center gap-2 justify-end mb-4">
          <button
            @click="openForm"
            class="btn-primary"
          >
            <i class="fa-solid fa-plus"></i>
            <span>Add Manual</span>
          </button>
        </div>

        <div v-if="loading && manuals.length === 0" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
          <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
        </div>

        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Section</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Position</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">File</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Date</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, idx) in manuals"
                  :key="item.id"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-200 capitalize">{{ item.sectionName }}</td>
                  <td class="py-3 px-4 border-b border-gray-200">{{ item.position }}</td>
                  <td class="py-3 px-4 border-b border-gray-200">
                    <a
                      :href="baseUrl + '/configuration/user-manual/' + item.filename"
                      target="_blank"
                      class="text-primary hover:text-blue-700 flex items-center space-x-1"
                      title="Click to view document"
                    >
                      <i class="fa-solid fa-eye"></i>
                      <span>View</span>
                    </a>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 text-center">
                    <span>{{ item.createdAt }}</span>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 text-center">
                    <button
                      @click="deleteUserManual(item.id)"
                      class="btn-sm-danger mx-auto"
                    >
                      <i class="fa-solid fa-trash"></i>
                      <span>Delete</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="manuals.length === 0">
                  <td colspan="5" class="py-8 px-4 text-center text-gray-500">
                    No manuals found. Click "Add Manual" to create one.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../../stores";
import AppModal from "@/components/ui/AppModal.vue";

interface Manual {
  id: string
  sectionName: string
  position: number
  filename: string
  createdAt: string
}

interface ManualForm {
  name: string | null
  position: number | null
  file: File | null
}

const store = useConfigurationsStore();
const showAddManual = ref(false)
const manuals = ref<Manual[]>([])
const loading = ref(false)
const baseUrl = ref(import.meta.env.VITE_API_BASE_URL)

const form = ref<ManualForm>({
  name: null,
  position: null,
  file: null
})

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    form.value.file = target.files[0]
  }
}

const openForm = () => {
  showAddManual.value = true
}

const closeForm = () => {
  showAddManual.value = false
  form.value = {
    name: null,
    position: null,
    file: null
  }
}

const save = () => {
  if (!form.value.name || !form.value.position || !form.value.file) {
    return
  }

  loading.value = true
  const formData = new FormData()
  formData.set('position', String(form.value.position))
  formData.set('sectionName', form.value.name)
  formData.set('file', form.value.file)

  store.createManual(formData);
}

const deleteUserManual = async (id: string) => {
  // @ts-ignore - showConfirmDialog is injected globally
  const confirmed = await window.showConfirmDialog()
  if (confirmed) {
    store.deleteManual(id);
  }
}

const fetchData = () => {
  store.fetchManuals();
}

onMounted(() => {
  fetchData()
})

watch(
  () => store.manuals,
  (data) => {
    if (data && data.data) {
      manuals.value = data.data
    }
  }
)

watch(
  () => store.manualCreated,
  (created) => {
    if (created) {
      loading.value = false
      closeForm()
      // @ts-ignore - successToast is injected globally
      window.successToast('Success', 'Manual created successfully')
      fetchData()
    }
  }
)

watch(
  () => store.manualDeleted,
  (deleted) => {
    if (deleted) {
      loading.value = false
      // @ts-ignore - successToast is injected globally
      window.successToast('Success', 'Manual deleted successfully')
      fetchData()
    }
  }
)

watch(
  () => store.manualsError,
  (error) => {
    if (error) {
      loading.value = false
      // @ts-ignore - errorToast is injected globally
      window.errorToast('Error', error.message)
    }
  }
)
</script>

